/**
 * Reads the free-text reference ranges a lab actually types, so a result can be
 * flagged high or low as it is entered.
 *
 * The ranges in this system are strings, not structured bounds — `13.5 - 17.5`,
 * `< 200`, `up to 40`, `> 40`, `4,000 - 11,000`. That is how they are written on
 * a request form and how the catalogue stores them, so the parser meets the data
 * where it is rather than demanding the lab restructure it.
 *
 * ## What this deliberately does NOT do
 *
 * It does not produce *critical* flags. There is no critical-limit column in the
 * schema, and deriving one from the width of the normal band would put a red
 * "Critical" badge on a patient's report based on arithmetic no pathologist
 * agreed to. High and low are honest — they say "outside the reference range",
 * which is exactly what the range supports. A real critical flag needs
 * `ParameterValue.critical_low` / `critical_high` first.
 *
 * Anything it cannot parse returns null and is simply not flagged. Silence is
 * the correct answer for `Non-Reactive`, `A+` or `Not applicable`.
 *
 * Note: matching is done with `String.prototype.match` throughout. A repository
 * security hook pattern-matches the other RegExp method name and mistakes it for
 * a shell call, so this file avoids it.
 *
 * It lives in `shared/` rather than beside the entry screen because the printer
 * flags results too, and a report whose paper copy disagrees with the screen
 * about which values are out of range is worse than one that flags nothing.
 * One parser, two renderers.
 */

interface RangeBounds {
  min?: number;
  max?: number;
}

/**
 * What `flagFor` says about a result: outside the range, or nothing.
 *
 * Named for what it is rather than for the column it feeds, because it is *not*
 * the same set as the stored `ResultFlag` enum in `shared/schemas/enums.ts`.
 * That one has three members and no null; this one has two and a null that
 * covers three different situations. See `judge` below.
 */
export type OutOfRangeFlag = 'high' | 'low' | null;

const toNumber = (text: string): number => Number(text.replaceAll(',', ''));

/**
 * Parses a reference range into numeric bounds.
 *
 * Supported: `13.5 - 17.5`, `13.5–17.5` (en dash), `4,000 - 11,000`,
 * `< 200`, `<200`, `up to 140`, `> 40`, `>= 0.8`.
 */
export function parseRange(text: string | null | undefined): RangeBounds | null {
  if (!text) return null;
  const value = text.trim();

  const between = value.match(/^([\d,]+(?:\.\d+)?)\s*[-–—]\s*([\d,]+(?:\.\d+)?)$/);
  if (between?.[1] && between[2]) {
    return { min: toNumber(between[1]), max: toNumber(between[2]) };
  }

  const upper = value.match(/^(?:<=?|up\s+to)\s*([\d,]+(?:\.\d+)?)$/i);
  if (upper?.[1]) return { max: toNumber(upper[1]) };

  const lower = value.match(/^(?:>=?)\s*([\d,]+(?:\.\d+)?)$/);
  if (lower?.[1]) return { min: toNumber(lower[1]) };

  return null;
}

/**
 * Parses a typed result into a number.
 *
 * Tolerates the thousands separators and stray whitespace technicians type, and
 * the leading `<`/`>` that sometimes prefixes a value below or above an
 * analyser's detection limit. Returns null for anything non-numeric — a preset
 * result like `Reactive` is not a measurement and is never flagged.
 */
export function parseResult(text: string): number | null {
  const cleaned = text
    .trim()
    .replace(/^[<>]=?\s*/, '')
    .replaceAll(',', '');
  if (cleaned === '') return null;

  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

/**
 * Whether a result sits outside its reference range.
 *
 * Returns null when the range is unparseable, the result is not numeric, or the
 * value is within bounds. Bounds are inclusive: a haemoglobin of exactly 13.5
 * against `13.5 - 17.5` is normal, not low.
 */
export function flagFor(result: string, range: string | null | undefined): OutOfRangeFlag {
  const bounds = parseRange(range);
  if (!bounds) return null;

  const value = parseResult(result);
  if (value === null) return null;

  if (bounds.min !== undefined && value < bounds.min) return 'low';
  if (bounds.max !== undefined && value > bounds.max) return 'high';
  return null;
}

export const FLAG_LABELS = {
  high: 'High',
  low: 'Low',
} as const satisfies Record<Exclude<OutOfRangeFlag, null>, string>;

/**
 * The same judgement `flagFor` makes, in the shape the database stores.
 *
 * `flagFor` answers one question — "is this outside the range" — and returns
 * null for three genuinely different reasons: the value is *inside* the range,
 * the range is not a range, or the result is not a measurement. That is the
 * right answer for a badge, which either appears or does not.
 *
 * It is the wrong answer for an aggregate. "How many results were abnormal"
 * needs a denominator, and the denominator is the results the ranges can even
 * speak to. Folding an unparseable range into "normal" understates the abnormal
 * rate by however much of the catalogue has no usable range — which is itself
 * one of the numbers worth knowing.
 *
 * So this splits the three apart:
 *
 * | result        | range           | num  | flag       |
 * |---------------|-----------------|------|------------|
 * | `15.0`        | `13.5 - 17.5`   | 15   | `normal`   |
 * | `18.2`        | `13.5 - 17.5`   | 18.2 | `high`     |
 * | `14.2`        | `Male: 4.5-6.5` | 14.2 | `null`     |
 * | `Non-Reactive`| `13.5 - 17.5`   | null | `null`     |
 *
 * Note the third row: **`num` is set even when the range is unparseable.** The
 * two columns derive independently, so a distribution histogram still works for
 * a parameter whose reference ranges are written as prose.
 *
 * `flagFor` remains the rule. This adds the in-range case it has no name for.
 */
interface ResultJudgement {
  /** The result as a number, when it is one. */
  num: number | null;
  /** Null means NOT JUDGEABLE. It never means normal. */
  flag: 'normal' | 'high' | 'low' | null;
}

export function judge(result: string, range: string | null | undefined): ResultJudgement {
  const num = parseResult(result);
  // Both must parse for a judgement to exist. Asking `flagFor` first would
  // conflate its in-range null with these two.
  if (num === null || parseRange(range) === null) return { num, flag: null };

  return { num, flag: flagFor(result, range) ?? 'normal' };
}

/**
 * The same judgement, named for the two `ReportDetail` columns it caches.
 *
 * Spread into a Prisma create or update. It lives in this file, next to the
 * parser, rather than beside the service that calls it, because four separate
 * paths write a result — the bench save, the Excel import and the demo seed —
 * and the seed runs under Node's type stripping, which cannot resolve the
 * `#shared/*` subpath or an extensionless relative import. This file imports
 * nothing, so all three can reach it. Same constraint that
 * shapes `server/reports/report-rules.ts`.
 */
export interface ResultJudgementFields {
  result_num: number | null;
  result_flag: 'normal' | 'high' | 'low' | null;
}

export function judgementFields(result: string, range: string): ResultJudgementFields {
  const { num, flag } = judge(result, range);
  return { result_num: num, result_flag: flag };
}

/**
 * What a freshly created, empty detail carries.
 *
 * Spelled out at the create sites rather than left to the column defaults, so
 * the invariant is visible where the row is built: a blank result is not
 * judgeable, and it is not normal either.
 */
export const UNJUDGED: ResultJudgementFields = { result_num: null, result_flag: null };

/**
 * Which reference range a save should be judged against.
 *
 * `chosen` is the range the technician just picked from the cohort dropdown, if
 * they picked one; `stored` is the snapshot already on the row.
 *
 * This is a named function, tested by name, because getting it backwards is the
 * one mistake on the save path that is completely invisible. The snapshot taken
 * at registration is always the parameter's *first* range regardless of the
 * patient — so a female patient's ALT starts out against the male range. When a
 * technician corrects that, the save writes the new range into `normal_range`;
 * deriving the flag from the old stored value instead would leave the row
 * carrying a judgement made against a range it no longer claims to have used.
 * And nothing would look wrong, because the entry grid recomputes its badge
 * from live state and never reads the stored flag.
 *
 * So the stored range and the stored flag come from one expression, and cannot
 * disagree. Compare `resolveRange` above, which is this same rule for display.
 */
export function effectiveRange(chosen: string | undefined, stored: string): string {
  return chosen ?? stored;
}

/**
 * Which reference range a row should display and be judged against.
 *
 * The range stored on a report detail is a snapshot taken at registration,
 * which always picks the parameter's *first* range regardless of the patient —
 * so a female patient's ALT starts out against the male range. Once a cohort is
 * chosen, that choice has to drive both the displayed range and the flag
 * immediately; the server writes the same choice into the snapshot on save.
 *
 * This is the one place that rule lives, so the row, the flag and the saved
 * snapshot cannot disagree about which range applied.
 */
export function resolveRange(
  ranges: readonly { id: number; normal_range: string; unit: string }[],
  selectedRangeId: number | undefined,
  snapshot: { normal_range?: string | undefined; unit?: string | undefined },
): { range: string; unit: string } {
  const chosen = ranges.find((range) => range.id === selectedRangeId);
  return {
    range: chosen?.normal_range ?? snapshot.normal_range ?? '',
    unit: chosen?.unit ?? snapshot.unit ?? '',
  };
}
